import { InfoRounded } from "@mui/icons-material";
import { Alert, Box, Stack, Typography } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import AcxMainTextField from "components/UI/AcxMainTextField";
import moment from "moment";
import {
    contextAlertIconColor,
    contextAlertSx,
} from "../../utils/contextAlert";

export function DecisionDialog(props: {
    open: boolean;
    title: string;
    titleIcon?: React.ReactNode;
    description: string;
    label: string;
    reasonCaption?: string;
    confirmLabel: string;
    reason: string;
    onReasonChange: (reason: string) => void;
    onClose: () => void;
    onConfirm: () => void;
    loading: boolean;
    destructive?: boolean;
    signedByLabel?: string;
    signedByValue?: string;
    onSignedByChange?: (value: string) => void;
    signedDateLabel?: string;
    banner?: React.ReactNode;
}) {
    const signatureRequired = !!props.onSignedByChange;
    return (
        <AcxDialog
            isOpen={props.open}
            onClose={props.onClose}
            title={
                props.titleIcon ? (
                    <Stack direction="row" alignItems="center" gap={1}>
                        {props.titleIcon}
                        {props.title}
                    </Stack>
                ) : (
                    props.title
                )
            }
            maxWidth="sm"
            fullWidth
            text={props.description}
            buttonsDisabled={props.loading}
            dialogContentChildren={
                <Stack gap={2.5}>
                    <Box>
                        <AcxMainTextField
                            id={
                                props.destructive
                                    ? "rejection-reason"
                                    : "changes-reason"
                            }
                            labelText={props.label}
                            value={props.reason}
                            onChange={(event) =>
                                props.onReasonChange(event.target.value)
                            }
                            multiline
                            rows={4}
                            maxLength={2000}
                            required
                            fullWidth
                        />
                        {props.reasonCaption && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                mt={0.5}
                                display="block"
                            >
                                {props.reasonCaption}
                            </Typography>
                        )}
                    </Box>

                    {props.signedByLabel && (
                        <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
                            <AcxMainTextField
                                id="decision-signed-by"
                                labelText={props.signedByLabel}
                                placeholderText={
                                    signatureRequired
                                        ? "Type your full name"
                                        : undefined
                                }
                                value={props.signedByValue}
                                onChange={
                                    props.onSignedByChange
                                        ? (event) =>
                                              props.onSignedByChange!(
                                                  event.target.value,
                                              )
                                        : undefined
                                }
                                isDisabled={!signatureRequired}
                                fullWidth
                                containerStyle={{ flex: 1 }}
                            />
                            <AcxMainTextField
                                id="decision-signed-date"
                                labelText={props.signedDateLabel}
                                value={moment().format("MMM D, YYYY · HH:mm")}
                                isDisabled
                                fullWidth
                                containerStyle={{ flex: 1 }}
                            />
                        </Stack>
                    )}

                    {props.banner && (
                        <Alert
                            severity={props.destructive ? "error" : "info"}
                            icon={
                                <InfoRounded
                                    fontSize="small"
                                    sx={{
                                        color: contextAlertIconColor(
                                            props.destructive
                                                ? "error"
                                                : "info",
                                        ),
                                    }}
                                />
                            }
                            sx={(theme) => ({
                                ...contextAlertSx(
                                    theme,
                                    props.destructive ? "error" : "info",
                                ),
                                "& .MuiAlert-message": {
                                    fontSize: "13px",
                                },
                            })}
                        >
                            {props.banner}
                        </Alert>
                    )}
                </Stack>
            }
        >
            <AcxButton
                color={props.destructive ? "error" : "primary"}
                loading={props.loading}
                disabled={
                    !props.reason.trim() ||
                    (signatureRequired && !props.signedByValue?.trim())
                }
                onClick={props.onConfirm}
            >
                {props.confirmLabel}
            </AcxButton>
        </AcxDialog>
    );
}
